import cx from 'classnames';

import { LiveEventAction, OnStageUser, UserRole } from 'types';
import { choosePaletteColor, getUserInitials } from 'utils';
import { eventSessionSelector, userSelector } from 'store/selectors';
import { useLiveEventActions, useStore } from 'hooks';
import { useLiveEvent } from 'containers/LiveEvent';
import { Avatar, Divider, Switch, Tooltip } from 'components';
import {
  CloseNewIcon,
  DeleteIcon,
  MicrophoneOffNewIcon,
  MicrophoneOnNewIcon,
  StarIcon,
  ThreeDotsIcon
} from 'components/Icon';

import styles from './Guests.Stage.module.scss';

export type StageGuestProps = {
  stageUser: OnStageUser;
  seatIndex: number;
  isMoreOptionsOpen?: boolean;
  onToggleMoreOptions?(user: OnStageUser): void;
  onRemoveFromStage?(user: OnStageUser): void;
  onSwitchRole?(user: OnStageUser, promote: boolean): void;
  onMute?(user: OnStageUser): void;
};

const displayRole = (userRole: UserRole) => {
  switch (userRole) {
    case UserRole.HOST:
      return 'Host';
    case UserRole.MODERATOR:
      return 'Moderator';
    case UserRole.SPEAKER:
      return 'Speaker';
    default:
      return null;
  }
};

const StageGuest = ({
  stageUser,
  seatIndex,
  isMoreOptionsOpen = false,
  onToggleMoreOptions,
  onMute,
  onRemoveFromStage,
  onSwitchRole
}: StageGuestProps): JSX.Element => {
  const liveEvent = useLiveEvent();
  const eventSession = useStore(eventSessionSelector(liveEvent.eventCode));
  const localUser = useStore(userSelector(liveEvent.eventCode, eventSession.username));
  const liveEventActions = useLiveEventActions(liveEvent.eventCode);

  const isMyself = stageUser.username === localUser.username;

  const initials = getUserInitials(stageUser.name);
  const fallbackColor = choosePaletteColor(initials, seatIndex);

  const handleOnMute = () => onMute?.(stageUser);

  const handleOnRemove = async () => onRemoveFromStage?.(stageUser);

  const handleOnToggleModerator = async (checked: boolean) => onSwitchRole?.(stageUser, checked);

  const handleOnToggleMore = () => {
    onToggleMoreOptions?.(stageUser);
  };

  const renderMuteButton = () => {
    const hasPermission = liveEventActions.hasPermission(LiveEventAction.FORCE_MUTE_SPEAKER);

    if (!hasPermission || isMyself) return null;

    const available = stageUser.joined && stageUser.audioTrack;
    const on = stageUser.audioTrack && !stageUser.audioMuted;

    return (
      <button
        type="button"
        className={cx(styles.action, styles.mute, {
          [styles.muted]: !on
        })}
        disabled={!available}
        onClick={handleOnMute}
      >
        {on ? <MicrophoneOnNewIcon /> : <MicrophoneOffNewIcon />}
      </button>
    );
  };

  const renderModeratorSwitch = () => {
    const hasPermission =
      liveEventActions.hasPermission(LiveEventAction.DEMOTE_MODERATOR) ||
      liveEventActions.hasPermission(LiveEventAction.PROMOTE_SPEAKER);

    if (!hasPermission || isMyself) return null;

    const canBeDone = stageUser.joined && stageUser.role !== UserRole.HOST;

    return (
      <Switch
        className={styles.moderator}
        title="Make moderator"
        disabled={!canBeDone}
        checked={stageUser.role === UserRole.MODERATOR}
        onChange={handleOnToggleModerator}
      />
    );
  };

  const renderRemoveButton = () => {
    const hasPermission = liveEventActions.hasPermission(LiveEventAction.REMOVE_SPEAKER_FROM_STAGE);

    if (!hasPermission || isMyself) return null;

    const canBeDone = stageUser.role !== UserRole.HOST;

    return (
      <button type="button" className={styles.remove} disabled={!canBeDone} onClick={handleOnRemove}>
        <DeleteIcon />
        <span>Remove from stage</span>
      </button>
    );
  };

  const renderMoreButton = () => {
    const hasPermission =
      liveEventActions.hasPermission(LiveEventAction.REMOVE_SPEAKER_FROM_STAGE) &&
      liveEventActions.hasPermission(LiveEventAction.PROMOTE_SPEAKER) &&
      liveEventActions.hasPermission(LiveEventAction.DEMOTE_MODERATOR);

    if (!hasPermission || isMyself) return null;

    return (
      <Tooltip
        placement="bottom-end"
        visible={isMoreOptionsOpen}
        offset={10}
        title={
          <div className={styles.options}>
            {renderModeratorSwitch()}
            <Divider className={styles.separator} />
            {renderRemoveButton()}
          </div>
        }
      >
        {ref => (
          <button
            ref={ref}
            type="button"
            className={cx(styles.action, styles.more, { [styles.active]: isMoreOptionsOpen })}
            onClick={handleOnToggleMore}
          >
            {isMoreOptionsOpen ? <CloseNewIcon /> : <ThreeDotsIcon />}
          </button>
        )}
      </Tooltip>
    );
  };

  const renderActions = () => {
    switch (localUser.role) {
      case UserRole.HOST:
      case UserRole.MODERATOR: {
        return (
          <div className={styles.actions}>
            {renderMuteButton()}
            {renderMoreButton()}
          </div>
        );
      }

      default: {
        return null;
      }
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.avatar}>
        <Avatar className={styles.avatar} src={stageUser.image} initials={initials} fallbackColor={fallbackColor} />
        {stageUser.member && (
          <div className={styles.badge}>
            <StarIcon />
          </div>
        )}
      </div>

      <div className={styles.info}>
        <h4 className={styles.title}>{stageUser.name}</h4>
        <p className={styles.subtitle}>
          {displayRole(stageUser.role)} {isMyself && '(me)'}
        </p>
      </div>
      {renderActions()}
    </div>
  );
};

export default StageGuest;
